// util

// components
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { UserProfile } from "@clockwise/schema";
import { formatEventTitle } from "@clockwise/web-commons/src/components/calendar/calendar-event/utils/formatEventTitle";
import { EventPreview } from "@clockwise/web-commons/src/ui/event-preview/EventPreview";
import { EventPreviewLoading } from "@clockwise/web-commons/src/ui/event-preview/EventPreviewLoading";
import { useUpdateActiveEvent } from "@clockwise/web-commons/src/util/ActiveEventContext";
import { formatDuration } from "@clockwise/web-commons/src/util/duration.util";
import { AnimatePresence, motion } from "framer-motion";
import { compact, flatten, partition } from "lodash";
import { DateTime } from "luxon";
import React, { useCallback, useEffect, useMemo } from "react";
import { useFeatureFlag } from "../../launch-darkly";
import {
  getCategoryLabels,
  getEventCategory,
  useCalendarColors,
} from "../../util/event-category-coloring.util";
import { usePlannerContext } from "./Context";

export interface PlannerMiniDayProps {
  selectedDate: DateTime;
  userId: string;
  openChalendar: () => void;
  onChangeDate: (date: DateTime) => void;
}

export interface Tray {
  nextEventTitle?: (title: string, videoLink?: string) => void;
  setWidth?: (width: number) => void;
}

export interface Versions {
  appVersion: string;
}

declare global {
  interface Window {
    tray: Tray;
    versions: Versions;
  }
}

export const PlannerMiniDay = ({ userId, selectedDate, openChalendar }: PlannerMiniDayProps) => {
  const {
    normalEvents: normalEventsNullable,
    calendarDataLoading,
    colorSettings,
    getEventForCard,
  } = usePlannerContext();

  const [now, setNow] = React.useState(DateTime.now());
  const { getEventColors } = useCalendarColors();
  const [showClockwiseAI] = useFeatureFlag("ClockwiseAI");

  // Set a 30 second interval to update a state value that will cause the component to re-render
  // this is to update the "ending in X minutes" header on the event preview
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(DateTime.now());
    }, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateActiveEvent = useUpdateActiveEvent();

  // const { submitMessage } = useChatContext(); // TODO: Refactor submitMessage from ChatUserInput to the Provider to enable dropping messages from anywhere.

  const onRescheduleEvent = useCallback(
    (prompt: string) => {
      openChalendar();
      console.log("prompt: ", prompt);
      // void submitMessage(prompt, []);
    },
    [openChalendar],
  );

  const toRender = useMemo((): JSX.Element[] => {
    const normalEvents = normalEventsNullable ?? {};
    const eventsSelectedDay = normalEvents[selectedDate.toISODate()] ?? [];
    let addedNextUp = false;
    let addedEndingIn = false;
    let addedPast = false;
    const eventsPartitioned = partition(
      compact(eventsSelectedDay.map((c) => getEventForCard?.(c))),
      (event) => DateTime.fromISO(event?.endTime.dateOrDateTime) > now,
    );
    const events = [...eventsPartitioned[0], ...eventsPartitioned[1]].map((event, i) => {
      const colors = getEventColors(event, colorSettings, userId, false, false);
      const eventCategory = getEventCategory(event, userId);
      const categoryString = getCategoryLabels(eventCategory);

      const startTime = DateTime.fromISO(event.startTime.dateOrDateTime);
      const endTime = DateTime.fromISO(event.endTime.dateOrDateTime);
      const isToday = selectedDate.toISODate() === DateTime.now().toISODate();

      const addNextUp = !addedNextUp && startTime > now;
      const addEndingIn = !addedEndingIn && startTime < now && endTime > now;
      const addPast = !addedPast && endTime < now;
      if (addPast) addedPast = true;
      if (addNextUp) addedNextUp = true;
      if (addEndingIn) addedEndingIn = true;

      const renderDivider = (body: string, key: string) => (
        <h3
          className="cw-bg-neutral cw-text-muted cw-text-14 cw-px-4 cw-py-[10px] cw-font-medium"
          key={key}
        >
          {body}
        </h3>
      );

      return [
        addNextUp &&
          renderDivider(
            `Next up in ${formatDuration(startTime.diff(now, "minutes").minutes)}`,
            "next-up-header",
          ),
        addPast && renderDivider("Past", "past-header"),
        // isToday && addNextUp,
        isToday &&
          addEndingIn &&
          renderDivider(
            `Ending in ${formatDuration(endTime.diff(now, "minutes").minutes)}`,
            "ending-in-header",
          ),
        i > 0 && !addEndingIn && !addNextUp && (
          <div key={`${i}-divider`} className="cw-h-px cw-bg-neutral" />
        ),
        <EventPreview
          onClick={() => {
            updateActiveEvent({
              externalEventId: event.eventKey.externalEventId,
              calendarId: event.calendarIds[0],
            });
          }}
          key={event.eventKey.externalEventId + "-event-preview"}
          showSubMenu={showClockwiseAI && false} // False for now until proper UI and rescheduling works better.
          showTime={
            i > 0
              ? getEventForCard?.(eventsSelectedDay[i - 1])?.startTime.dateOrDateTime !==
                event.startTime.dateOrDateTime
              : true
          }
          title={formatEventTitle(event.title, event.locked)}
          startTime={DateTime.fromISO(event.startTime.dateOrDateTime)}
          endTime={DateTime.fromISO(event.endTime.dateOrDateTime)}
          isNow={startTime < now && endTime > now}
          onRescheduleClick={onRescheduleEvent}
          attendees={compact(
            event.attendees.map((a) => {
              const personList = getValue(event?.attendeePeople, "PersonList")?.list;
              const calIdToPerson: Record<string, UserProfile> = {};
              if (personList) {
                personList.forEach((person) => {
                  if (person.profile) {
                    calIdToPerson[person.primaryEmail] = person.profile;
                  }
                });
              }

              // Just in case the person is not in the list, don't show them. This shouldn't happen,
              // but saw this in dev with some stale devenv/wisedevr data.
              const profile = calIdToPerson[a.urnValue];
              if (!profile) {
                return null;
              }
              return { profile: calIdToPerson[a.urnValue], rsvp: a.responseStatus };
            }),
          )}
          videoLink={event.videoLink}
          color={colors.foreground}
          category={categoryString.label || undefined}
        />,
      ];
    });

    if (!events.length) {
      return [
        <h3 key="no-events" className="cw-heading-base cw-mt-6 cw-ml-6">
          No events
        </h3>,
      ];
    }

    return compact(flatten(events));
  }, [
    normalEventsNullable,
    selectedDate,
    getEventForCard,
    now,
    getEventColors,
    colorSettings,
    userId,
    showClockwiseAI,
    onRescheduleEvent,
    updateActiveEvent,
  ]);

  if (calendarDataLoading) {
    return (
      <div className="cw-flex cw-flex-col cw-gap-4 cw-ml-4 cw-mt-10">
        <EventPreviewLoading />
        <EventPreviewLoading />
      </div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div transition={{ staggerChildren: 0.05 }} className="cw-w-full cw-flex cw-flex-col">
        {toRender.map((e) => (
          <motion.div
            key={e.key}
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {e}
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>
  );
};
